/**
 * Google Analytics 4. The measurement ID and the event names live here and
 * nowhere else.
 *
 * Page views: the `gtag('config')` call in the root route's head sends the
 * first one, and GA4's enhanced measurement ("page changes based on browser
 * history events", on by default in the data stream) sends one for every
 * client-side navigation the router makes. Nothing in this file fires them,
 * so the application does not add a second page-view emitter.
 *
 * What follows is the part GA4 cannot guess: which taps mean a buyer is
 * trying to reach us. This site has no contact form — every route to a
 * conversation is an outbound link. A `whatsapp_click` is a contact attempt,
 * not confirmation of a sent message, qualified lead or sale.
 */

export const GA_MEASUREMENT_ID = "G-KMN67YYK99";

/** Only the canonical production host may send to the live GA4 property. */
export function isProductionAnalyticsHost(host: string): boolean {
  return host.toLowerCase().replace(/:\d+$/, "") === "iraqcert.com";
}

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

type OutboundEvent = "whatsapp_click" | "email_click" | "phone_click" | "linkedin_click";

function eventFor(href: string): OutboundEvent | null {
  if (/\/\/(wa\.me|api\.whatsapp\.com|web\.whatsapp\.com)/i.test(href)) return "whatsapp_click";
  if (href.startsWith("mailto:")) return "email_click";
  if (href.startsWith("tel:")) return "phone_click";
  if (/\/\/([a-z]+\.)?linkedin\.com/i.test(href)) return "linkedin_click";
  return null;
}

/**
 * One delegated listener on the document, in the capture phase. Links added
 * by a later page — a new vendor page, a rebuilt footer — are measured
 * without touching this file or the component that renders them.
 *
 * Returns its own cleanup, so it can be handed straight to useEffect.
 */
export function trackOutboundClicks(): () => void {
  if (!isProductionAnalyticsHost(window.location.hostname)) return () => {};
  const onClick = (e: MouseEvent) => {
    const target = e.target;
    if (!(target instanceof Element)) return;
    const anchor = target.closest("a[href]");
    if (!anchor) return;

    const href = anchor.getAttribute("href") ?? "";
    const name = eventFor(href);
    if (!name || typeof window.gtag !== "function") return;

    window.gtag("event", name, {
      // Enquiry links can contain company details or message text. Measure
      // the destination without sending query or fragment values to GA4.
      link_url: href.split(/[?#]/, 1)[0],
      link_text: (anchor.textContent ?? "").trim().slice(0, 100),
      page_path: window.location.pathname,
      site_language: document.documentElement.lang.startsWith("ar") ? "ar" : "en",
      // Fixed identifiers distinguish homepage CTAs without sending user input.
      cta_location: ["home-hero-whatsapp", "home-hero-phone"].includes(
        anchor.getAttribute("data-cta") ?? "",
      )
        ? anchor.getAttribute("data-cta")
        : "other",
    });
  };

  document.addEventListener("click", onClick, true);
  return () => document.removeEventListener("click", onClick, true);
}
